import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CalendarPlus, CalendarMinus } from 'lucide-react';
import { useHabitContext } from '../../context/HabitContext';
import { useTodos } from '../../hooks/useTodos';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import { CalendarEvent } from '../../types/todo';
import { toLocalDateStr } from '../../utils/dateUtils';
import Spinner from '../common/Spinner';
import './DailyTimeline.scss';

// Internal block that lives on the calendar grid
interface CalBlock {
  id: string;
  label: string;
  startMin: number; // minutes from midnight
  endMin: number;
  type: 'habit' | 'todo' | 'gcal';
  done: boolean;
  color?: string;
  allDay?: boolean;
  overdue?: boolean;
  /** Raw habit/todo id (without prefix) for mutations */
  rawId: string;
  /** Google Calendar event id if this item has been synced */
  gcalEventId?: string | null;
}

const HOUR_HEIGHT = 32; // px per hour row (compact)
const START_HOUR = 0;   // calendar starts at midnight
const END_HOUR = 23;    // calendar ends at 11 PM
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const SNAP_MINUTES = 15;

interface DailyCalendarProps {
  date: string;
  onEmptySlotClick?: (time: string) => void;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function minutesToTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function isoToMinutes(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

function snapMinutes(min: number): number {
  return Math.round(min / SNAP_MINUTES) * SNAP_MINUTES;
}

function isSameCalendarDay(dateKey: string, now: Date): boolean {
  return dateKey === toLocalDateStr(now);
}

export function nowMarkerScrollTop(markerY: number, viewportHeight: number): number {
  const viewport = viewportHeight > 0 ? viewportHeight : 520;
  return Math.max(0, markerY - viewport / 2);
}

const DailyCalendar: React.FC<DailyCalendarProps> = ({
  date,
  onEmptySlotClick,
}) => {
  const { formatMessage: t } = useIntl();
  const { categories, isLoading: habitsLoading, getHabitEntriesFor, toggleHabitOn, updateHabitScheduledTime } = useHabitContext();
  const { todos, isLoading: todosLoading, toggleComplete, updateTodoTime } = useTodos(date);
  const { connected, enabled, events: gcalEvents, loading: gcalLoading, connect, disconnect, createEvent, deleteEvent } = useGoogleCalendar(date);
  const gridRef = useRef<HTMLDivElement>(null);

  const now = new Date();
  const viewingToday = isSameCalendarDay(date, now);
  const [viewYear, viewMonth1, viewDay] = date.split('-').map(Number);
  const month = viewMonth1 - 1;
  const year = viewYear;
  const day = viewDay;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  // ── Drag state ────────────────────────────────────────────────────────────
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOffsetMin, setDragOffsetMin] = useState(0);
  const [dragCurrentMin, setDragCurrentMin] = useState(0);
  const dragMovedRef = useRef(false);

  // Build blocks
  const blocks: CalBlock[] = [];
  const allDayBlocks: CalBlock[] = [];

  // Habits with scheduledTime
  categories.forEach(cat => {
    cat.habits.forEach(habit => {
      if (habit.scheduledTime) {
        const entries = getHabitEntriesFor(habit.id, month, year);
        const entry = entries.find(e => e.day === day);
        const startMin = timeToMinutes(habit.scheduledTime);
        const done = entry?.completed || false;
        const isOverdue = viewingToday && !done && startMin < nowMinutes;
        blocks.push({
          id: `h-${habit.id}`,
          rawId: habit.id,
          label: habit.name,
          startMin,
          endMin: startMin + 30, // default 30min block
          type: 'habit',
          done,
          overdue: isOverdue,
          color: habit.color,
          gcalEventId: entry?.gcalEventId ?? null,
        });
      }
    });
  });

  // Todos with scheduledTime
  todos.forEach(todo => {
    if (todo.scheduledTime) {
      const startMin = timeToMinutes(todo.scheduledTime);
      const isOverdue = viewingToday && !todo.completed && timeToMinutes(todo.scheduledTime) < nowMinutes;
      blocks.push({
        id: `t-${todo.id}`,
        rawId: todo.id,
        label: todo.title,
        startMin,
        endMin: startMin + 30,
        type: 'todo',
        done: todo.completed,
        overdue: isOverdue,
        gcalEventId: todo.gcalEventId ?? null,
      });
    }
  });

  // Google Calendar events
  gcalEvents.forEach((ev: CalendarEvent) => {
    if (ev.allDay) {
      allDayBlocks.push({
        id: `g-${ev.id}`,
        rawId: ev.id,
        label: ev.title,
        startMin: 0,
        endMin: 0,
        type: 'gcal',
        done: false,
        color: ev.color,
        allDay: true,
      });
    } else {
      const startMin = ev.startTime.includes('T') ? isoToMinutes(ev.startTime) : timeToMinutes(ev.startTime);
      const endMin = ev.endTime.includes('T') ? isoToMinutes(ev.endTime) : timeToMinutes(ev.endTime);
      blocks.push({
        id: `g-${ev.id}`,
        rawId: ev.id,
        label: ev.title,
        startMin,
        endMin: endMin > startMin ? endMin : startMin + 30,
        type: 'gcal',
        done: false,
        color: ev.color,
      });
    }
  });

  // Position helpers
  const minToY = (min: number) => ((min - START_HOUR * 60) / 60) * HOUR_HEIGHT;
  const yToMin = (y: number) => START_HOUR * 60 + (y / HOUR_HEIGHT) * 60;
  const durationToPx = (startMin: number, endMin: number) =>
    Math.max(20, ((endMin - startMin) / 60) * HOUR_HEIGHT);

  // Simple overlap column assignment
  const sorted = [...blocks].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const columns: Map<string, { col: number; totalCols: number }> = new Map();

  const active: CalBlock[] = [];
  for (const block of sorted) {
    // Remove blocks that have ended
    while (active.length > 0 && active[0].endMin <= block.startMin) {
      active.shift();
    }
    active.push(block);
    active.sort((a, b) => a.endMin - b.endMin);

    const usedCols = new Set(active.map(b => columns.get(b.id)?.col).filter(c => c !== undefined));
    let col = 0;
    while (usedCols.has(col)) col++;
    columns.set(block.id, { col, totalCols: active.length });
  }

  // Second pass: set totalCols to the max overlapping group size
  for (const block of sorted) {
    const overlapping = sorted.filter(
      b => b.startMin < block.endMin && b.endMin > block.startMin
    );
    const c = columns.get(block.id)!;
    c.totalCols = overlapping.length;
  }

  const handleEmptySlotClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!onEmptySlotClick) return;
    if ((e.target as HTMLElement).closest('.day-cal__block')) return;
    const gridEl = gridRef.current;
    if (!gridEl) return;
    const rect = gridEl.getBoundingClientRect();
    const y = e.clientY - rect.top + gridEl.scrollTop;
    const snapped = snapMinutes(Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, yToMin(y))));
    onEmptySlotClick(minutesToTime(snapped));
  }, [onEmptySlotClick]);
  const handleBlockClick = useCallback((block: CalBlock) => {
    if (block.type === 'gcal') return; // can't toggle Google events
    if (block.type === 'habit') {
      toggleHabitOn(block.rawId, day, month, year);
    } else {
      toggleComplete(block.rawId);
    }
  }, [toggleHabitOn, toggleComplete, day, month, year]);

  // ── Add / remove from Google Calendar ────────────────────────────────────
  const handleGcalToggle = useCallback((e: React.MouseEvent, block: CalBlock) => {
    e.stopPropagation();
    if (!connected || block.type === 'gcal') return;
    if (block.gcalEventId) {
      deleteEvent.mutate(block.gcalEventId);
    } else {
      const endMin = block.endMin > block.startMin ? block.endMin : block.startMin + 30;
      createEvent.mutate({
        date,
        title: block.label,
        startTime: minutesToTime(block.startMin),
        endTime: minutesToTime(endMin),
      });
    }
  }, [connected, createEvent, deleteEvent, date]);

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = useCallback((clientY: number, block: CalBlock) => {
    if (block.type === 'gcal') return;
    const gridEl = gridRef.current;
    if (!gridEl) return;
    const rect = gridEl.getBoundingClientRect();
    const pointerY = clientY - rect.top + gridEl.scrollTop;
    const pointerMin = yToMin(pointerY);
    dragMovedRef.current = false;
    setDragId(block.id);
    setDragOffsetMin(pointerMin - block.startMin);
    setDragCurrentMin(block.startMin);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent, block: CalBlock) => {
    if (block.type === 'gcal') return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    if (typeof e.currentTarget.setPointerCapture === 'function') {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    handleDragStart(e.clientY, block);
  }, [handleDragStart]);

  // Keep blocks in a ref so the drag effect can access latest without re-subscribing
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  useEffect(() => {
    if (!dragId) return;
    const gridEl = gridRef.current;
    if (!gridEl) return;

    const handlePointerMove = (e: PointerEvent) => {
      const rect = gridEl.getBoundingClientRect();
      const pointerY = e.clientY - rect.top + gridEl.scrollTop;
      const rawMin = yToMin(pointerY) - dragOffsetMin;
      const snapped = snapMinutes(Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, rawMin)));
      if (snapped !== dragCurrentMin) {
        dragMovedRef.current = true;
      }
      setDragCurrentMin(snapped);
    };

    const handlePointerUp = () => {
      const block = blocksRef.current.find(b => b.id === dragId);
      if (block && dragMovedRef.current && dragCurrentMin !== block.startMin) {
        const newTime = minutesToTime(dragCurrentMin);
        if (block.type === 'habit') {
          updateHabitScheduledTime(block.rawId, newTime);
        } else if (block.type === 'todo') {
          updateTodoTime(block.rawId, newTime);
        }
      } else if (block && !dragMovedRef.current) {
        // Click without drag — toggle
        handleBlockClick(block);
      }
      setDragId(null);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [dragId, dragOffsetMin, dragCurrentMin, updateHabitScheduledTime, updateTodoTime, handleBlockClick]);

  // Now-indicator position
  const nowY = minToY(nowMinutes);
  const showNow = viewingToday
    && nowMinutes >= START_HOUR * 60
    && nowMinutes <= END_HOUR * 60 + 60;
  const calLoading = habitsLoading || todosLoading || (enabled && gcalLoading);

  useLayoutEffect(() => {
    if (calLoading || !showNow) return;
    const scroller = gridRef.current;
    if (!scroller) return;
    scroller.scrollTop = nowMarkerScrollTop(nowY, scroller.clientHeight);
  }, [calLoading, showNow, nowY]);

  return (
    <div className="day-cal">
      <div className="day-cal__header">
        <span className="day-cal__title">{t({ id: 'daily.cal.title' })}</span>
        <div className="day-cal__header-actions">
          {enabled && connected && (
            <span className="day-cal__gcal-badge">
              {t({ id: 'daily.cal.connectedBadge' })}
              <button
                className="day-cal__gcal-disconnect"
                onClick={disconnect}
                type="button"
                aria-label={t({ id: 'daily.cal.disconnectBtn' })}
              >
                {t({ id: 'daily.cal.disconnectBtn' })}
              </button>
            </span>
          )}
          {enabled && !connected && (
            <button
              className="day-cal__gcal-connect"
              onClick={connect}
              type="button"
            >
              {t({ id: 'daily.cal.connectBtn' })}
            </button>
          )}
        </div>
      </div>

          {calLoading ? (
            <Spinner block className="day-cal__loader" />
          ) : (
            <>
          {/* All-day events */}
          {allDayBlocks.length > 0 && (
            <div className="day-cal__allday">
              <span className="day-cal__allday-label">{t({ id: 'daily.cal.allDay' })}</span>
              <div className="day-cal__allday-items">
                {allDayBlocks.map(b => (
                  <span key={b.id} className="day-cal__allday-chip">{b.label}</span>
                ))}
              </div>
            </div>
          )}

          {/* Scrollable grid */}
          <div className="day-cal__scroll" ref={gridRef} data-testid="day-cal-scroll">
            <div className="day-cal__grid" style={{ height: HOURS.length * HOUR_HEIGHT }} onClick={handleEmptySlotClick}>
              {/* Hour lines */}
              {HOURS.map(h => (
                <div
                  key={h}
                  className="day-cal__hour"
                  style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}
                >
                  <span className="day-cal__hour-label">{formatHour(h)}</span>
                  <div className="day-cal__hour-line" />
                </div>
              ))}

              {/* Now indicator */}
              {showNow && (
                <div className="day-cal__now" data-testid="day-cal-now" style={{ top: nowY }}>
                  <span className="day-cal__now-dot" />
                  <span className="day-cal__now-line" />
                </div>
              )}

              {/* Event blocks */}
              {sorted.map(block => {
                const { col, totalCols } = columns.get(block.id)!;
                const isDragging = dragId === block.id;
                const effectiveStart = isDragging ? dragCurrentMin : block.startMin;
                const duration = block.endMin - block.startMin;
                const top = minToY(effectiveStart);
                const height = durationToPx(effectiveStart, effectiveStart + duration);
                const gutterPx = block.type === 'gcal' ? 64 : 44;
                const width = block.overdue
                  ? `calc((100% - 64px) / ${totalCols} + 10px)`
                  : `calc((100% - 64px) / ${totalCols})`;
                const left = `calc(${gutterPx}px + (100% - 45px) * ${col} / ${totalCols})`;

                const typeClass = `day-cal__block--${block.type}`;
                const doneClass = block.done ? ' day-cal__block--done' : '';
                const overdueClass = block.overdue ? ' day-cal__block--overdue' : '';
                const draggable = block.type !== 'gcal';
                const draggingClass = isDragging ? ' day-cal__block--dragging' : '';

                return (
                  <div
                    key={block.id}
                    data-testid={`day-cal-block-${block.id}`}
                    className={`day-cal__block ${typeClass}${doneClass}${overdueClass}${draggingClass}`}
                    style={{
                      top,
                      height,
                      width,
                      left,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (block.type === 'gcal') return;
                    }}
                    onPointerDown={(e) => draggable && handlePointerDown(e, block)}
                  >
                    {block.type !== 'gcal' && (
                      <span className={`day-cal__block-check${block.done ? ' day-cal__block-check--done' : ''}`}>
                        {block.done ? '✓' : ''}
                      </span>
                    )}
                    <span className="day-cal__block-label">{block.label}</span>
                    {block.overdue ? (
                      <span className="day-cal__block-tag day-cal__block-tag--overdue">
                        {t({ id: 'daily.cal.overdue' })}
                      </span>
                    ) : (
                      <span className={`day-cal__block-tag day-cal__block-tag--${block.type}`}>
                        {block.type === 'habit' ? t({ id: 'daily.cal.habitTag' }) : block.type === 'todo' ? t({ id: 'daily.cal.taskTag' }) : t({ id: 'daily.cal.calTag' })}
                      </span>
                    )}
                    {enabled && connected && block.type !== 'gcal' && (
                      <button
                        className={`day-cal__block-gcal-btn${block.gcalEventId ? ' day-cal__block-gcal-btn--remove' : ''}`}
                        onClick={(e) => handleGcalToggle(e, block)}
                        onPointerDown={(e) => e.stopPropagation()}
                        type="button"
                        title={block.gcalEventId ? t({ id: 'daily.cal.removeFromGcal' }) : t({ id: 'daily.cal.addToGcal' })}
                        aria-label={block.gcalEventId ? t({ id: 'daily.cal.removeFromGcal' }) : t({ id: 'daily.cal.addToGcal' })}
                      >
                        {block.gcalEventId ? <CalendarMinus size={10} /> : <CalendarPlus size={10} />}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
            </>
          )}
    </div>
  );
};

export default DailyCalendar;
